import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Mail, Phone, Home, Calendar, DollarSign, Hash,
  Droplet, Zap, Package, Shield, CreditCard, Edit, Trash2, Plus, LogOut,
} from 'lucide-react';
import {
  getTenantUtilityBills, addUtilityBill, updateUtilityBill, deleteUtilityBill,
  updateTenantRent, updateTenant, fetchApartments, fetchCategories, fetchUnits,
  getTenantWaterReadings,
} from './api';
import { useAppTheme } from '../../contexts/ThemeContext';
import UpdateBalanceDeposit from './UpdateBalanceDeposit';
import UpdateWaterReading   from './UpdateWaterReading';
import UpdateDetails from './UpdateDetails';
import MoveOut from './MoveOut';
import ManageUtilityCharge from './ManageUtilityCharge';

// Defined outside component — prevents remount-on-render which steals input focus
const ModalOverlay = ({ children }) =>
  createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
      {children}
    </div>,
    document.body
  );

const TenantCardView = ({ tenant: initialTenant, onClose, onUpdate }) => {
  const { palette, Colors } = useAppTheme();

  // Local copy — keeps card live without a full re-mount on every save
  const [tenant, setTenant] = useState(initialTenant);

  // Sync local copy when parent refreshes the prop (e.g. after handleTenantUpdate)
  useEffect(() => { setTenant(initialTenant); }, [initialTenant]);

  const [activeTab, setActiveTab]             = useState('details');
  const [utilityBills, setUtilityBills]       = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [success, setSuccess]                 = useState(null);

  // dialogs
  const [editTenantDialog, setEditTenantDialog]   = useState(false);
  const [editRentDialog, setEditRentDialog]       = useState(false);
  const [editUtilityDialog, setEditUtilityDialog] = useState(false);
  const [addUtilityDialog, setAddUtilityDialog]   = useState(false);

  // delete confirmation
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [billToDelete, setBillToDelete]               = useState(null);
  const [moveOutDialog, setMoveOutDialog] = useState(false);

  const [tenantForm, setTenantForm] = useState({
    firstName: '', lastName: '', phone: '', propertyId: '', categoryId: '', unitId: '',
  });
  const [newRent, setNewRent]               = useState(tenant?.rent || 0);
  const [editingUtility, setEditingUtility] = useState(null);
  const [newUtility, setNewUtility]         = useState({ type: '', amount: '', chargeType: 'monthly' });
  const [apartments, setApartments]         = useState([]);
  const [categories, setCategories]         = useState([]);
  const [units, setUnits]                   = useState([]);

  // latest water reading — passed down to UpdateWaterReading for rate auto-fill
  const [latestWaterReading, setLatestWaterReading] = useState(null);

  // ── effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab === 'financial' && tenant.id) {
      fetchUtilityBills();
      fetchLatestWaterReading();
    }
  }, [activeTab, tenant.id]);

  useEffect(() => {
    if (editTenantDialog) {
      loadApartments();
      const [firstName, ...rest] = tenant.name.split(' ');
      setTenantForm({ firstName: firstName || '', lastName: rest.join(' ') || '', phone: tenant.phone || '' });
    }
  }, [editTenantDialog]);

  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(null); setError(null); }, 5000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  // ── fetchers ──────────────────────────────────────────────────────────────
  const fetchUtilityBills = async () => {
    setLoading(true); setError(null);
    try {
      const data = await getTenantUtilityBills(tenant.id);
      setUtilityBills(data.utilityBills || []);
      if (data.currentRent) setNewRent(data.currentRent);
    } catch { setError('Failed to load utility bills'); }
    finally { setLoading(false); }
  };

  const fetchLatestWaterReading = async () => {
    try {
      const res  = await getTenantWaterReadings(tenant.id);
      const list = Array.isArray(res) ? res : (res.results || []);
      if (list.length > 0) setLatestWaterReading(list[0]);
    } catch {}
  };

  const loadApartments = async () => { try { setApartments(await fetchApartments()); } catch {} };
  const loadCategories = async (id) => { try { setCategories(await fetchCategories(id)); setUnits([]); } catch {} };
  const loadUnits      = async (id) => { try { setUnits(await fetchUnits(id)); } catch {} };

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleUpdateTenant = async () => {
    try {
      setLoading(true); setError(null);
      const data = { firstName: tenantForm.firstName, lastName: tenantForm.lastName, phone: tenantForm.phone };
      if (tenantForm.unitId) data.unitId = tenantForm.unitId;
      await updateTenant(tenant.id, data);
      setSuccess('Tenant details updated'); setEditTenantDialog(false);
      if (onUpdate) onUpdate();
    } catch (e) { setError(typeof e === 'string' ? e : 'Failed to update tenant details'); }
    finally { setLoading(false); }
  };

  const handleUpdateRent = async () => {
    try {
      setLoading(true); setError(null);
      await updateTenantRent(tenant.id, { rent: parseFloat(newRent) });
      setSuccess('Rent updated'); setEditRentDialog(false);
      setTenant(t => ({ ...t, rent: parseFloat(newRent) }));
      await fetchUtilityBills();
      if (onUpdate) onUpdate();
    } catch (e) { setError(typeof e === 'string' ? e : 'Failed to update rent'); }
    finally { setLoading(false); }
  };

  const handleAddUtility = async () => {
    if (!newUtility.type || !newUtility.amount) { setError('Please fill in all required fields'); return; }
    try {
      setLoading(true); setError(null);
      await addUtilityBill(tenant.id, {
        type: newUtility.type, amount: parseFloat(newUtility.amount),
        status: 'Unpaid', chargeType: newUtility.chargeType,
      });
      setSuccess('Charge added'); setAddUtilityDialog(false);
      setNewUtility({ type: '', amount: '', chargeType: 'monthly' });
      await fetchUtilityBills();
    } catch (e) { setError(typeof e === 'string' ? e : 'Failed to add charge'); }
    finally { setLoading(false); }
  };

  const handleUpdateUtility = async () => {
    if (!editingUtility?.amount) { setError('Please enter a valid amount'); return; }
    try {
      setLoading(true); setError(null);
      await updateUtilityBill(tenant.id, editingUtility.id, {
        type: editingUtility.type, amount: parseFloat(editingUtility.amount),
        status: editingUtility.status || 'Unpaid', chargeType: 'monthly',
      });
      setSuccess('Charge updated'); setEditUtilityDialog(false); setEditingUtility(null);
      await fetchUtilityBills();
    } catch (e) { setError(typeof e === 'string' ? e : 'Failed to update charge'); }
    finally { setLoading(false); }
  };

  const promptDeleteUtility = (bill) => {
    setBillToDelete(bill);
    setDeleteConfirmDialog(true);
  };

  const confirmDeleteUtility = async () => {
    if (!billToDelete) return;
    setDeleteConfirmDialog(false);
    try {
      setLoading(true); setError(null);
      await deleteUtilityBill(tenant.id, billToDelete.id);
      setSuccess(`"${billToDelete.type}" charge deleted`);
      setBillToDelete(null);
      await fetchUtilityBills();
    } catch (e) { setError(typeof e === 'string' ? e : 'Failed to delete charge'); }
    finally { setLoading(false); }
  };

  // ── helpers ───────────────────────────────────────────────────────────────
  const getStatusColor = (status) => {
    if (status === 'Active')      return { bg: Colors.status.success, text: '#fff' };
    if (status === 'Past Due')    return { bg: Colors.status.error,   text: '#fff' };
    if (status === 'Ending Soon') return { bg: Colors.accent.blue,    text: '#fff' };
    if (status === 'Moved Out')   return { bg: '#6B7280',             text: '#fff' }; // ✅ grey
    if (status === 'Inactive')    return { bg: '#9CA3AF',             text: '#fff' }; // ✅ lighter grey
    return { bg: palette.text.muted, text: '#fff' };
  };
  const getChargeIcon = (name) => {
    const n = name.toLowerCase();
    if (n.includes('wifi') || n.includes('internet')) return Zap;
    if (n.includes('water'))    return Droplet;
    if (n.includes('garbage') || n.includes('trash')) return Package;
    if (n.includes('security')) return Shield;
    return Package;
  };

  if (!tenant) return null;

  const sc     = getStatusColor(tenant.status);
  const bg     = palette.background;
  const txt    = palette.text;
  const border = palette.border;
  const P      = Colors.primary.main;

  const inputClass = `w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2`;
  const inputStyle = { backgroundColor: bg.tertiary, border: `1px solid ${border}`, color: txt.primary };

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: bg.secondary, border: `1px solid ${border}`, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="relative p-4 text-white"
        style={{ background: `linear-gradient(135deg, ${Colors.primary.dark} 0%, ${Colors.primary.main} 100%)` }}>
        <button onClick={onClose}
          className="absolute top-2 right-2 p-1.5 rounded-full transition-colors"
          style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold border-2"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.3)' }}>
            {tenant.initials}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-1">{tenant.name}</h2>
            <div className="flex items-center gap-3 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{tenant.email}</span>
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{tenant.phone}</span>
            </div>
            <div className="mt-2">
              <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full"
                style={{ backgroundColor: sc.bg, color: sc.text }}>{tenant.status}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error   && <div className="mx-6 mt-4 p-3 rounded-lg text-sm" style={{ backgroundColor: `${Colors.status.error}15`,   border: `1px solid ${Colors.status.error}40`,   color: Colors.status.error   }}>{error}</div>}
      {success && <div className="mx-6 mt-4 p-3 rounded-lg text-sm" style={{ backgroundColor: `${Colors.status.success}15`, border: `1px solid ${Colors.status.success}40`, color: Colors.status.success }}>{success}</div>}
      
      {tenant.status === 'Moved Out' && (
        <div className="mx-6 mt-4 p-3 rounded-lg flex items-center gap-2 text-sm font-medium"
          style={{ backgroundColor: 'rgba(107,114,128,0.12)', border: '1px solid rgba(107,114,128,0.3)', color: '#6B7280' }}>
          <LogOut className="w-4 h-4 flex-shrink-0" />
          This tenant has moved out
          {tenant.movedOutAt && <span style={{ color: '#9CA3AF' }}>· {tenant.movedOutAt}</span>}
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div style={{ borderBottom: `1px solid ${border}`, backgroundColor: bg.tertiary }}>
        <nav className="flex px-6">
          {['details', 'financial', 'history'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-5 py-3 font-medium text-sm border-b-2 transition-colors capitalize"
              style={{
                borderBottomColor: activeTab === tab ? P : 'transparent',
                color: activeTab === tab ? P : txt.secondary,
                backgroundColor: activeTab === tab ? bg.secondary : 'transparent',
              }}>
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div className="p-6">

        {/* DETAILS TAB */}
        {activeTab === 'details' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Personal */}
              <div className="rounded-xl p-4" style={{ backgroundColor: `${Colors.accent.purple}10`, border: `1px solid ${Colors.accent.purple}30` }}>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: Colors.accent.purple }}>
                  <CreditCard className="w-4 h-4" /> Personal Information
                </h3>
                <div className="space-y-3">
                  {[
                    ['National ID', tenant.idNumber, <CreditCard className="w-4 h-4" />],
                    ['Email',       tenant.email,    <Mail className="w-4 h-4" />],
                    ['Phone',       tenant.phone,    <Phone className="w-4 h-4" />],
                    
                  ].map(([label, val, icon]) => (
                    <div key={label} className="flex items-start gap-2.5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${Colors.accent.purple}25`, color: Colors.accent.purple }}>{icon}</div>
                      <div>
                        <p className="text-xs font-medium" style={{ color: Colors.accent.purple }}>{label}</p>
                        <p className="text-sm font-semibold" style={{ color: txt.primary }}>{val}</p>
                      </div>
                    </div>
                  ))}
                  {/* Additional phones */}
                    {tenant.additionalPhones?.length > 0 && (
                      <div className="flex items-start gap-2.5">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${Colors.accent.purple}25`, color: Colors.accent.purple }}>
                          <Phone className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium mb-1" style={{ color: Colors.accent.purple }}>
                            Additional Contacts
                          </p>
                          <div className="space-y-1.5">
                            {tenant.additionalPhones.map(p => (
                              <div key={p.id} className="flex items-center justify-between">
                                <p className="text-sm font-semibold" style={{ color: txt.primary }}>{p.phone}</p>
                                {p.description && (
                                  <span className="text-xs px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: `${Colors.accent.purple}20`, color: Colors.accent.purple }}>
                                    {p.description}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
              {/* Property */}
              <div className="rounded-xl p-4" style={{ backgroundColor: `${P}10`, border: `1px solid ${P}30` }}>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: P }}>
                  <Home className="w-4 h-4" /> Property Information
                </h3>
                <div className="space-y-3">
                  {[
                    ['Tenant ID',       tenant.accountId,                           <Hash className="w-4 h-4" />],
                    ['Property',        tenant.property,                            <Home className="w-4 h-4" />],
                    ['Unit & Category', `${tenant.unit} • ${tenant.category}`,      <Package className="w-4 h-4" />],
                  ].map(([label, val, icon]) => (
                    <div key={label} className="flex items-start gap-2.5">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${P}25`, color: P }}>{icon}</div>
                      <div>
                        <p className="text-xs font-medium" style={{ color: P }}>{label}</p>
                        <p className="text-sm font-semibold" style={{ color: txt.primary }}>{val}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Lease */}
            <div className="rounded-xl p-4" style={{ backgroundColor: `${Colors.status.success}10`, border: `1px solid ${Colors.status.success}30` }}>
              <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: Colors.status.success }}>
                <Calendar className="w-4 h-4" /> Lease Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  ['Lease Start',       tenant.leaseStart],
                  ['Lease End',         tenant.leaseEnd],
                  ['Security Deposit', `KES ${Number(tenant.securityDeposit?.total ?? tenant.securityDeposit ?? 0).toLocaleString()}`],
                  ['Monthly Rent',     `KES ${tenant.rent?.toLocaleString()}`],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-lg p-3" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                    <p className="text-xs font-medium mb-1" style={{ color: Colors.status.success }}>{label}</p>
                    <p className="text-base font-bold" style={{ color: txt.primary }}>{val}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FINANCIAL TAB */}
        {activeTab === 'financial' && (
          <div className="space-y-6">

            {/* 4 summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">

              {/* Monthly Rent */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: `${P}12`, border: `1px solid ${P}30` }}>
                <p className="text-xs font-medium mb-2" style={{ color: P }}>Monthly Rent</p>
                <p className="text-xl font-bold" style={{ color: txt.primary }}>KES {tenant.rent?.toLocaleString()}</p>
              </div>

              {/* Balance */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: `${Colors.status.success}12`, border: `1px solid ${Colors.status.success}30` }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium" style={{ color: Colors.status.success }}>Current Balance</p>
                  <button
                    onClick={() => {/* opened by UpdateBalanceDeposit internally */}}
                    className="hidden" /* placeholder — edit icon is rendered inside the child */
                  />
                  <UpdateBalanceDeposit
                    tenant={tenant}
                    mode="balance"
                    onUpdated={(patch) => {
                      setTenant(t => ({ ...t, ...patch }));
                      setSuccess(`Balance updated to KES ${Number(patch.balance).toLocaleString()}`);
                      if (onUpdate) onUpdate();
                    }}
                  />
                </div>
                <p className="text-xl font-bold"
                  style={{ color: tenant.balance < 0 ? Colors.status.error : txt.primary }}>
                  KES {Number(tenant.balance ?? 0).toLocaleString()}
                </p>
              </div>

              {/* Security Deposit */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: `${Colors.accent.purple}12`, border: `1px solid ${Colors.accent.purple}30` }}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium" style={{ color: Colors.accent.purple }}>Security Deposit</p>
                  <UpdateBalanceDeposit
                    tenant={tenant}
                    mode="deposit"
                    onUpdated={(patch) => {
                      setTenant(t => ({ ...t, ...patch }));
                      setSuccess(`Deposit updated to KES ${Number(patch.securityDeposit).toLocaleString()}`);
                      if (onUpdate) onUpdate();
                    }}
                  />
                </div>
                <p className="text-xl font-bold" style={{ color: txt.primary }}>
                  KES {Number(tenant.securityDeposit?.total ?? tenant.securityDeposit ?? 0).toLocaleString()}
                </p>
              </div>

              {/* Water Bill */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: `${Colors.accent.blue}12`, border: `1px solid ${Colors.accent.blue}30` }}>
                <p className="text-xs font-medium mb-2" style={{ color: Colors.accent.blue }}>Water Bill</p>
                <p className="text-xl font-bold" style={{ color: txt.primary }}>
                  KES {Number(tenant.waterBill ?? 0).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Water Reading section */}
            <div className="rounded-xl p-5"
              style={{ backgroundColor: bg.secondary, border: `1px solid ${border}`, borderLeft: `4px solid ${Colors.accent.blue}` }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Droplet className="w-5 h-5" style={{ color: Colors.accent.blue }} />
                  <h4 className="font-semibold" style={{ color: txt.primary }}>Water Reading</h4>
                </div>
                <UpdateWaterReading
                  tenant={tenant}
                  latestWaterReading={latestWaterReading}
                  onSaved={(res) => {
                    // Patch local state immediately from the API response
                    // so balance and history update without waiting for re-fetch
                    const newTxn = {
                      type: 'charge',
                      description: `Water Bill: ${res.totalUnits} units @ KES ${res.rate}/unit`,
                      date: new Date(res.readingDate).toLocaleDateString(),
                      amount: -res.totalCharge,
                      method: 'System',
                    };
                    setTenant(t => ({
                      ...t,
                      waterBill:    res.totalCharge,
                      balance:      res.newBalance,
                      transactions: [newTxn, ...(t.transactions || [])],
                    }));
                    setSuccess(`Water saved! ${res.totalUnits} units — KES ${Number(res.totalCharge).toLocaleString()}`);
                    fetchLatestWaterReading();
                    if (onUpdate) onUpdate();
                  }}
                />
              </div>

              {latestWaterReading ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      ['Previous',   latestWaterReading.previousReading],
                      ['Current',    latestWaterReading.currentReading],
                      ['Units Used', latestWaterReading.totalUnits],
                      ['Charge',    `KES ${parseFloat(latestWaterReading.totalCharge || 0).toLocaleString()}`],
                    ].map(([label, val], i) => (
                      <div key={label} className="rounded-lg p-3" style={{ backgroundColor: bg.tertiary }}>
                        <p className="text-xs mb-1" style={{ color: txt.secondary }}>{label}</p>
                        <p className="font-bold text-sm"
                          style={{ color: i === 3 ? Colors.status.error : txt.primary }}>{val}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs mt-2" style={{ color: txt.muted }}>
                    Rate: KES {latestWaterReading.rate}/unit • {latestWaterReading.readingDate}
                  </p>
                </>
              ) : (
                <p className="text-sm italic" style={{ color: txt.muted }}>
                  No readings yet — click Record Reading to start.
                </p>
              )}
            </div>

            {/* Monthly Charges */}
            <div className="rounded-xl p-5" style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold" style={{ color: txt.primary }}>Monthly Charges</h4>
                <button onClick={() => setAddUtilityDialog(true)}
                  className="px-4 py-2 text-white rounded-lg font-medium text-sm flex items-center gap-2"
                  style={{ backgroundColor: P }}>
                  <Plus className="w-4 h-4" /> Add Charge
                </button>
              </div>

              {/* Rent row */}
              <div className="flex items-center justify-between p-4 rounded-lg mb-3"
                style={{ backgroundColor: `${P}10`, border: `1px solid ${P}30` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: P }}>
                    <Home className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium" style={{ color: txt.primary }}>Rent</p>
                    <p className="text-sm" style={{ color: txt.secondary }}>Monthly charge</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-semibold" style={{ color: txt.primary }}>KES {tenant.rent?.toLocaleString()}</p>
                  <button onClick={() => setEditRentDialog(true)} className="p-2 rounded-lg" style={{ color: P }}>
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Utility bills */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: P }}></div>
                  <p className="mt-2 text-sm" style={{ color: txt.secondary }}>Loading...</p>
                </div>
              ) : utilityBills.length > 0 ? (
                <div className="space-y-3">
                  {utilityBills.map(bill => {
                    const IconComp = getChargeIcon(bill.type);
                    return (
                      <div key={bill.id} className="flex items-center justify-between p-4 rounded-lg"
                        style={{ backgroundColor: `${Colors.accent.purple}10`, border: `1px solid ${Colors.accent.purple}30` }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: Colors.accent.purple }}>
                            <IconComp className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium" style={{ color: txt.primary }}>{bill.type}</p>
                            <p className="text-sm" style={{ color: txt.secondary }}>Monthly charge</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-lg font-semibold" style={{ color: txt.primary }}>
                            KES {parseFloat(bill.amount || 0).toLocaleString()}
                          </p>
                          <button onClick={() => { setEditingUtility(bill); setEditUtilityDialog(true); }}
                            className="p-2 rounded-lg" style={{ color: P }}>
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => promptDeleteUtility(bill)}
                            className="p-2 rounded-lg" style={{ color: Colors.status.error }}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8" style={{ color: txt.muted }}>
                  <Package className="w-12 h-12 mx-auto mb-2" />
                  <p>No utility charges added yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold mb-4" style={{ color: txt.primary }}>Recent Transactions</h4>
            {(tenant.transactions || []).map((txn, i) => {
              const isPayment = txn.amount > 0;
              const color     = isPayment ? Colors.status.success : Colors.status.error;
              return (
                <div key={i} className="flex items-center justify-between p-4 rounded-xl"
                  style={{ backgroundColor: `${color}10`, border: `1px solid ${color}30` }}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: color }}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isPayment
                          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />}
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: txt.primary }}>{txn.description}</p>
                      <p className="text-sm" style={{ color: txt.secondary }}>{txn.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold" style={{ color }}>
                      {isPayment ? '+' : ''}KES {Math.abs(txn.amount).toLocaleString()}
                    </p>
                    <p className="text-xs" style={{ color: txt.muted }}>{txn.method}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div className="px-6 py-3 flex justify-end gap-2.5"
        style={{ backgroundColor: bg.tertiary, borderTop: `1px solid ${border}` }}>
        <button onClick={onClose}
          className="px-5 py-2 rounded-lg font-medium text-sm"
          style={{ backgroundColor: bg.secondary, border: `1px solid ${border}`, color: txt.primary }}>
          Close
        </button>
      
        {/* Only show action buttons for active tenants */}
        {tenant.isActive !== false && tenant.status !== 'Moved Out' && activeTab === 'details' && (
          <>
            {/* ✅ Move-Out button */}
            <button onClick={() => setMoveOutDialog(true)}
              className="px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 text-white"
              style={{ backgroundColor: Colors.status.error }}>
              <LogOut className="w-4 h-4" />
              Move Out
            </button>
      
            {/* Existing update button */}
            <button onClick={() => setEditTenantDialog(true)}
              className="px-5 py-2 text-white rounded-lg font-medium text-sm"
              style={{ backgroundColor: P }}>
              Update Details
            </button>
          </>
        )}
      </div>
      {/* ════════════════════════════════════════════
          MODALS (local to TenantCardView)
      ════════════════════════════════════════════ */}

      {/* Delete charge confirmation */}
      {deleteConfirmDialog && billToDelete && (
        <ModalOverlay>
          <div className="rounded-xl max-w-sm w-full p-6"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${Colors.status.error}15` }}>
                <Trash2 className="w-5 h-5" style={{ color: Colors.status.error }} />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-1" style={{ color: txt.primary }}>Delete Charge</h3>
                <p className="text-sm" style={{ color: txt.secondary }}>
                  Deleting <strong style={{ color: txt.primary }}>{billToDelete.type}</strong> means it will not be
                  processed in the next billing period. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setDeleteConfirmDialog(false); setBillToDelete(null); }}
                disabled={loading}
                className="px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}>
                Cancel
              </button>
              <button onClick={confirmDeleteUtility} disabled={loading}
                className="px-4 py-2 text-white rounded-lg font-medium"
                style={{ backgroundColor: Colors.status.error }}>
                {loading ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
 {/* Edit tenant details */}
        {editTenantDialog && (
                <ModalOverlay>
                  <div className="rounded-2xl w-full max-w-3xl overflow-hidden"
                    style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>
                    <UpdateDetails
                      tenant={tenant}
                      onClose={() => setEditTenantDialog(false)}
                      onUpdate={() => { if (onUpdate) onUpdate(); }}
                    />
                  </div>
                </ModalOverlay>
              )}

      {/* Edit Rent */}
      {editRentDialog && (
        <ModalOverlay>
          <div className="rounded-xl max-w-md w-full p-6"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>
            <h3 className="text-xl font-bold mb-4" style={{ color: txt.primary }}>Update Monthly Rent</h3>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>Amount (KES)</label>
              <input type="number" value={newRent}
                onChange={e => setNewRent(e.target.value)}
                className={inputClass} style={inputStyle} />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditRentDialog(false)} disabled={loading}
                className="px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${border}` }}>Cancel</button>
              <button onClick={handleUpdateRent} disabled={loading}
                className="px-4 py-2 text-white rounded-lg font-medium" style={{ backgroundColor: P }}>
                {loading ? 'Updating…' : 'Update'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {addUtilityDialog && (
        <ManageUtilityCharge
          tenant={tenant}
          bill={null}
          onSaved={async (res) => {
            setSuccess('Charge added');
            setAddUtilityDialog(false);
            if (res?.newBalance != null) setTenant(t => ({ ...t, balance: res.newBalance }));
            await fetchUtilityBills();
          }}
          onClose={() => setAddUtilityDialog(false)}
        />
      )}

    {editUtilityDialog && editingUtility && (
      <ManageUtilityCharge
        tenant={tenant}
        bill={editingUtility}
        onSaved={async () => {
          setSuccess('Charge updated');
          setEditUtilityDialog(false);
          setEditingUtility(null);
          await fetchUtilityBills();
        }}
        onClose={() => { setEditUtilityDialog(false); setEditingUtility(null); }}
      />
    )}

{moveOutDialog && (
  <ModalOverlay>
    <div className="rounded-2xl w-full max-w-4xl overflow-hidden"
      style={{ backgroundColor: bg.secondary, border: `1px solid ${border}` }}>
      <MoveOut
        tenant={tenant}
        onClose={() => setMoveOutDialog(false)}
        onMoveOut={(result) => {
          setMoveOutDialog(false);
          // Update local tenant state to reflect moved-out status
          setTenant(t => ({ ...t, status: 'Moved Out', isActive: false, movedOutAt: result.movedOutAt }));
          if (onUpdate) onUpdate();
          // Close the card after a short delay so user sees the status change
          setTimeout(() => onClose(), 800);
        }}
      />
    </div>
  </ModalOverlay>
)}

    </div>
  );
};

export default TenantCardView;